import type { Router, RouteRecordRaw } from 'vue-router';

declare module 'vue-router' {
  // Module augmentation must use `interface` so it merges with vue-router's RouteMeta;
  // a `type` alias would collide (TS2300), so the repo-wide prefer-type rule is disabled here.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface RouteMeta {
    // Feature locale files this route needs, loaded by the guard before the route renders.
    localeFeatures?: string[];
  }
}

type EnsureFeatures = (features: string[]) => Promise<void>;

// Tag a module's top-level route records with the features they need. vue-router merges meta
// parent -> child onto the matched route, so nested child routes inherit it.
export const withLocaleFeatures = (routes: RouteRecordRaw[], features: string[]): RouteRecordRaw[] =>
  routes.map((route) => ({ ...route, meta: { ...route.meta, localeFeatures: features } }));

// Load a route's declared features in parallel with its chunk (fire in beforeEach) and make sure
// they are present before it renders (await in beforeResolve). The loader picks the locale and dedups
// by (locale set, locale), so the two calls share one fetch; a failed fetch surfaces through the awaited
// beforeResolve guard, which vue-router routes to the app's router.onError handler.
export const installLocaleFeaturesGuard = (router: Router, ensureFeatures: EnsureFeatures) => {
  const load = (features: string[] = []) => ensureFeatures(features);

  router.beforeEach((to) => {
    load(to.meta.localeFeatures).catch(() => undefined);
  });

  router.beforeResolve(async (to) => {
    await load(to.meta.localeFeatures);
  });
};
